<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  extractDocumentTitle,
  getDocumentIcon,
  isImageDocument,
} from 'dashboard/utils/documentHelpers';

const props = defineProps({
  documents: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['selectionChanged', 'previewDocument']);

const { t } = useI18n();

const selectedDocuments = ref(new Set());
const expandedDocuments = ref(new Set());

const isSelected = document => {
  return selectedDocuments.value.has(document.id);
};

const isExpanded = document => {
  return expandedDocuments.value.has(document.id);
};

const toggleExpanded = document => {
  if (expandedDocuments.value.has(document.id)) {
    expandedDocuments.value.delete(document.id);
  } else {
    expandedDocuments.value.add(document.id);
  }
};

const toggleSelection = document => {
  if (selectedDocuments.value.has(document.id)) {
    selectedDocuments.value.delete(document.id);
  } else {
    selectedDocuments.value.add(document.id);
  }

  // Emit the selected documents as an array
  const selectedArray = props.documents.filter(doc =>
    selectedDocuments.value.has(doc.id)
  );
  emit('selectionChanged', selectedArray);
};

const handlePreview = document => {
  emit('previewDocument', document);
};

const formatDate = dateString => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString();
};
</script>

<template>
  <div class="p-6 bg-slate-50 dark:bg-slate-900 h-full overflow-y-auto">
    <!-- Articles Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
      <article
        v-for="document in documents"
        :key="document.id"
        class="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600 hover:-translate-y-1 transition-all duration-300 cursor-pointer relative group"
        @click="handlePreview(document)"
      >
        <!-- Selection Checkbox -->
        <button
          type="button"
          class="absolute top-4 right-4 flex items-center justify-center w-5 h-5 border-2 rounded transition-colors z-10"
          :class="{
            'bg-n-brand border-n-brand': isSelected(document),
            'border-slate-300 dark:border-slate-600 hover:border-n-brand':
              !isSelected(document),
          }"
          @click.stop="toggleSelection(document)"
        >
          <Icon
            v-if="isSelected(document)"
            icon="i-lucide-check"
            class="w-3 h-3 text-white"
          />
        </button>

        <!-- Article Header -->
        <div class="flex items-start space-x-3 mb-4">
          <div
            class="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-n-brand/10 to-n-brand/20 rounded-xl flex-shrink-0"
          >
            <Icon
              :icon="getDocumentIcon(document)"
              class="w-6 h-6 text-n-brand"
            />
          </div>
          <div class="flex-1 min-w-0">
            <h3
              class="text-lg font-semibold text-slate-900 dark:text-slate-100 line-clamp-2 leading-6 mb-1"
            >
              {{ extractDocumentTitle(document) }}
            </h3>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{ formatDate(document.updated_at) }}
            </p>
          </div>
        </div>

        <!-- Article Content -->
        <div class="mb-6">
          <!-- Image Preview for image documents -->
          <div
            v-if="isImageDocument(document)"
            class="mb-4 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700"
          >
            <img
              :src="document.metadata.reference"
              :alt="extractDocumentTitle(document)"
              class="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
              @error="$event.target.style.display = 'none'"
            />
          </div>

          <p
            v-if="document.content"
            class="text-sm text-slate-700 dark:text-slate-300 leading-relaxed"
            :class="{
              'line-clamp-6': !isExpanded(document),
            }"
          >
            {{ document.content }}
          </p>
          <button
            v-if="document.content && document.content.length > 300"
            type="button"
            class="mt-2 text-xs text-n-brand hover:text-n-brand/80 font-medium transition-colors"
            @click.stop="toggleExpanded(document)"
          >
            {{
              isExpanded(document)
                ? t('LIBRARIES.DOCUMENTS.SHOW_LESS')
                : t('LIBRARIES.DOCUMENTS.SHOW_MORE')
            }}
          </button>
        </div>

        <!-- Article Footer -->
        <div
          class="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-700"
        >
          <div class="flex items-center space-x-2">
            <span
              v-if="document.record"
              class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
            >
              {{ document.record.name || document.record.id }}
            </span>
          </div>
          <div class="flex items-center space-x-2">
            <button
              type="button"
              class="text-slate-400 hover:text-n-brand opacity-0 group-hover:opacity-100 transition-all duration-200"
              @click.stop="handlePreview(document)"
            >
              <Icon icon="i-lucide-external-link" class="w-4 h-4" />
            </button>
          </div>
        </div>
      </article>
    </div>

    <!-- Empty State -->
    <div
      v-if="documents.length === 0"
      class="flex flex-col items-center justify-center py-16"
    >
      <Icon
        icon="i-lucide-file-text"
        class="w-16 h-16 text-slate-400 dark:text-slate-600 mb-4"
      />
      <h3 class="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
        {{ t('LIBRARIES.DOCUMENTS.NO_DOCUMENTS') }}
      </h3>
      <p class="text-slate-600 dark:text-slate-400 text-center max-w-md">
        {{ t('LIBRARIES.DOCUMENTS.NO_DOCUMENTS_DESCRIPTION') }}
      </p>
    </div>
  </div>
</template>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-4 {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-6 {
  display: -webkit-box;
  -webkit-line-clamp: 6;
  line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
